import { Typography } from '@mui/material';
import { FlexBox } from 'components/Containers/FlexBox';
import { extractTagChartData, getKey } from '../../utils';
import { IndividualAreaChart } from './Area';
import { useAppSelector } from 'store/hook';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useMemo } from 'react';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { useTranslation } from 'react-i18next';

export function formatYAxis(value: any) {
  // Convert string numbers to actual numbers
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  // If not a valid number, return the original value
  if (isNaN(numValue)) {
    return value;
  }

  let updatedValue = numValue;
  if (numValue >= 1e12) {
    updatedValue = `${(numValue / 1e12).toFixed(2)} T`;
  } else if (numValue >= 1e9) {
    updatedValue = `${(numValue / 1e9).toFixed(2)} B`;
  } else if (numValue >= 1e6) {
    updatedValue = `${(numValue / 1e6).toFixed(2)} M`;
  } else if (numValue >= 1e3) {
    updatedValue = `${(numValue / 1e3).toFixed(2)} k`;
  } else {
    updatedValue = numValue % 1 === 0 ? numValue : numValue.toFixed(2);
  }
  return updatedValue;
}

export const Chart = ({
  data,
  tagName,
}: {
  data: {
    tagName: string;
    tagDetails: any[];
    isValidRange: boolean;
  };
  tagName: string;
}) => {
  const { t } = useTranslation();
  const { tagGrainData } = useAppSelector((state) => state.tagReducer);

  const activeButton = useAppSelector(
    (state) => state.sensorAnalysisReducer.regularFilters.activeButton
  );

  const keyToUse = useMemo(() => getKey(activeButton), [activeButton]);

  const selectedAsset = useAppSelector(
    (state) => state.sensorAnalysisReducer.selectedAsset
  );

  const selectedOperation = useAppSelector(
    (state) => state.sensorAnalysisReducer.aggregation
  );

  const dataType = useAppSelector(
    (state) => state.sensorAnalysisReducer.dataType
  );

  const startDate = useAppSelector(
    (state) => state.sensorAnalysisReducer.regularFilters.startDate
  );

  const endDate = useAppSelector(
    (state) => state.sensorAnalysisReducer.regularFilters.endDate
  );

  const grainStartDate = useAppSelector(
    (state) => state.sensorAnalysisReducer.highGrainFilters.startDateTime
  );

  const grainEndDate = useAppSelector(
    (state) => state.sensorAnalysisReducer.highGrainFilters.endDateTime
  );

  const { unitSystem } = useCustomerInfo();

  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();

  const memoisedStartDate = useMemo(() => startDate, [startDate]);
  const memoisedEndDate = useMemo(() => endDate, [endDate]);

  const { filteredArray, unit } = useMemo(() => {
    if (timezoneBasedOnDisplayMode) {
      const temp = extractTagChartData(
        data,
        keyToUse,
        activeButton,
        memoisedStartDate,
        memoisedEndDate,
        selectedAsset,
        unitSystem,
        dataType,
        tagGrainData,
        tagName,
        timezoneBasedOnDisplayMode
      );
      return temp;
    } else {
      return {
        filteredArray: [],
        unit: '',
      };
    }
  }, [
    JSON.stringify(data),
    keyToUse,
    activeButton,
    memoisedStartDate,
    memoisedEndDate,
    selectedAsset,
    unitSystem,
    dataType,
    tagGrainData,
    tagName,
    timezoneBasedOnDisplayMode,
  ]);

  const memoisedGrainStartDate = useMemo(
    () => grainStartDate,
    [grainStartDate]
  );
  const memoisedGrainEndDate = useMemo(() => grainEndDate, [grainEndDate]);

  return (
    <FlexBox
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: '100%',
        width: '100%',
        height: '300px',
      }}
    >
      {filteredArray?.length > 0 ? (
        <IndividualAreaChart
          data={filteredArray}
          dataType={dataType}
          unit={unit}
          selectedOperation={selectedOperation}
          startDate={memoisedGrainStartDate}
          endDate={memoisedGrainEndDate}
        />
      ) : (
        <Typography
          sx={{
            fontSize: '12px',
          }}
        >
          {' '}
          {t('no_data_for_the_selected_time_range')}
        </Typography>
      )}
    </FlexBox>
  );
};
